import { Controller, SubmitHandler, useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "react-toastify";

import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form, FormControl, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Combobox } from "@/components/ui/combobox";
import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { RawRecipe } from "@/types/recipe";

const formSchema = z.object({
  reportName: z.string().min(1, { message: "Field is required." }),
  legacyNames: z.string().optional(),
  dutNames: z.string().optional(),
  deviceClass: z.string().min(1, { message: "Field is required." }),
  condition: z.string().min(1, { message: "Field is required." }),
  recipe: z.custom<RawRecipe>((value) => value != null, {
    message: "Field is required.",
  }),

  lsl: z.number().optional(),
  tgt: z.number().optional(),
  usl: z.number().optional(),

  jmpYmin: z.number().optional(),
  jmpYmax: z.number().optional(),
  jmpYinc: z.number().optional(),

  libMedian: z.number().optional(),
  libMean: z.number().optional(),
  libStd: z.number().optional(),
  libQ10: z.number().optional(),
  libQ90: z.number().optional(),
  libN: z.number().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

type NewSpecFormProps = {
  deviceClasses?: string[];
  conditions?: string[];
  recipes?: RawRecipe[];
  onSubmit?: SubmitHandler<FormSchema>;
};

const setNumberOrUndefined = (value: string) =>
  value === "" ? undefined : Number.isNaN(Number(value)) ? undefined : Number(value);

export function NewSpecForm({
  deviceClasses = [],
  conditions = [],
  recipes = [],
  onSubmit,
}: NewSpecFormProps) {
  const [deviceClassValues, setDeviceClassValues] = useState<string[]>(deviceClasses);
  const [conditionValues, setConditionValues] = useState<string[]>(conditions);
  const [openSections, setOpenSections] = useState({
    specLimits: false,
    jmpSettings: false,
    libraryStats: false,
  });

  const toggleSection = (key: keyof typeof openSections) =>
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));

  const collapseSections = () =>
    setOpenSections({ specLimits: false, jmpSettings: false, libraryStats: false });

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    mode: "all",
    defaultValues: {
      reportName: "",
      legacyNames: "",
      dutNames: "",
      deviceClass: "",
      condition: "",
      recipe: undefined,
      lsl: undefined,
      tgt: undefined,
      usl: undefined,
      jmpYmin: undefined,
      jmpYmax: undefined,
      jmpYinc: undefined,
      libMedian: undefined,
      libMean: undefined,
      libStd: undefined,
      libQ10: undefined,
      libQ90: undefined,
      libN: undefined,
    },
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = form;

  const handleFormSubmit: SubmitHandler<FormSchema> = async (data) => {
    if (onSubmit) {
      await onSubmit(data);
    }
    toast.success(`Spec added: ${data.reportName}`);
    form.reset();
    collapseSections();
  };

  const addUnique = (items: string[], value: string) => {
    const v = value.trim();
    if (!v) return items;
    return items.includes(v) ? items : [...items, v];
  };

  return (
    <Form {...form}>
      <form
        onSubmit={handleSubmit(handleFormSubmit)}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        <section className="space-y-3">
          <h3 className="text-sm font-medium">Basic Information</h3>
          <Separator />
          <div className="grid grid-cols-2 gap-6">
            <FormItem>
              <FormLabel htmlFor="reportName">Report Name</FormLabel>
              <FormControl>
                <Input required id="reportName" {...register("reportName")} />
              </FormControl>
              {errors.reportName && <FormMessage>{errors.reportName.message}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel htmlFor="legacyNames">Legacy Names</FormLabel>
              <FormControl>
                <Input id="legacyNames" {...register("legacyNames")} />
              </FormControl>
            </FormItem>

            <FormItem>
              <FormLabel htmlFor="dutNames">DUT Names</FormLabel>
              <FormControl>
                <Input id="dutNames" {...register("dutNames")} />
              </FormControl>
            </FormItem>
            <FormItem>
              <FormLabel>Recipe</FormLabel>
              <FormControl>
                <Controller
                  control={control}
                  name="recipe"
                  render={({ field }) => (
                    <Select
                      value={field.value ? String(field.value.id) : ""}
                      onValueChange={(value) => {
                        const selectedRecipe = recipes.find(
                          (recipe) => String(recipe.id) === value,
                        );
                        field.onChange(selectedRecipe);
                      }}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select recipe" />
                      </SelectTrigger>
                      <SelectContent>
                        {recipes.map((opt) => (
                          <SelectItem key={opt.id} value={String(opt.id)}>
                            {opt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
              </FormControl>
              {errors.recipe && <FormMessage>{errors.recipe.message as string}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel>Device Class</FormLabel>
              <FormControl>
                <Controller
                  control={control}
                  name="deviceClass"
                  render={({ field }) => (
                    <Combobox
                      fullWidth
                      modal
                      options={deviceClassValues.map((value) => ({
                        key: value,
                        value,
                        label: value,
                      }))}
                      placeholder="Select device class"
                      searchPlaceholder="Type to search or create"
                      value={field.value}
                      onChange={field.onChange}
                      onCreate={(value) => {
                        setDeviceClassValues((prev) => addUnique(prev, value));
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FormControl>
              {errors.deviceClass && <FormMessage>{errors.deviceClass.message}</FormMessage>}
            </FormItem>

            <FormItem>
              <FormLabel>Condition</FormLabel>
              <FormControl>
                <Controller
                  control={control}
                  name="condition"
                  render={({ field }) => (
                    <Combobox
                      fullWidth
                      modal
                      options={conditionValues.map((value) => ({
                        key: value,
                        value,
                        label: value,
                      }))}
                      placeholder="Select condition"
                      searchPlaceholder="Type to search or create"
                      value={field.value}
                      onChange={field.onChange}
                      onCreate={(value) => {
                        setConditionValues((prev) => addUnique(prev, value));
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </FormControl>
              {errors.condition && <FormMessage>{errors.condition.message}</FormMessage>}
            </FormItem>
          </div>
        </section>
        <section className="space-y-3">
          <button
            type="button"
            className="flex w-full items-center gap-1 text-sm font-medium"
            onClick={() => toggleSection("specLimits")}
          >
            <ChevronDown
              className="size-4 transition-transform duration-200"
              style={{ transform: openSections.specLimits ? "rotate(0deg)" : "rotate(-90deg)" }}
            />
            Spec Limits
          </button>
          <Separator />
          {openSections.specLimits && (
            <div className="grid grid-cols-3 gap-4">
              <FormItem>
                <FormLabel htmlFor="lsl">LSL</FormLabel>
                <FormControl>
                  <Input
                    id="lsl"
                    type="number"
                    step="any"
                    {...register("lsl", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="tgt">Target</FormLabel>
                <FormControl>
                  <Input
                    id="tgt"
                    type="number"
                    step="any"
                    {...register("tgt", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="usl">USL</FormLabel>
                <FormControl>
                  <Input
                    id="usl"
                    type="number"
                    step="any"
                    {...register("usl", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <button
            type="button"
            className="flex w-full items-center gap-1 text-sm font-medium"
            onClick={() => toggleSection("jmpSettings")}
          >
            <ChevronDown
              className="size-4 transition-transform duration-200"
              style={{ transform: openSections.jmpSettings ? "rotate(0deg)" : "rotate(-90deg)" }}
            />
            JMP Settings
          </button>
          <Separator />
          {openSections.jmpSettings && (
            <div className="grid grid-cols-3 gap-4">
              <FormItem>
                <FormLabel htmlFor="jmpYmin">Y Min</FormLabel>
                <FormControl>
                  <Input
                    id="jmpYmin"
                    type="number"
                    step="any"
                    {...register("jmpYmin", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="jmpYmax">Y Max</FormLabel>
                <FormControl>
                  <Input
                    id="jmpYmax"
                    type="number"
                    step="any"
                    {...register("jmpYmax", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="jmpYinc">Y Inc</FormLabel>
                <FormControl>
                  <Input
                    id="jmpYinc"
                    type="number"
                    step="any"
                    {...register("jmpYinc", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
            </div>
          )}
        </section>

        <section className="space-y-3">
          <button
            type="button"
            className="flex w-full items-center gap-1 text-sm font-medium"
            onClick={() => toggleSection("libraryStats")}
          >
            <ChevronDown
              className="size-4 transition-transform duration-200"
              style={{ transform: openSections.libraryStats ? "rotate(0deg)" : "rotate(-90deg)" }}
            />
            Library Statistics
          </button>
          <Separator />
          {openSections.libraryStats && (
            <div className="grid grid-cols-3 gap-4">
              <FormItem>
                <FormLabel htmlFor="libMedian">Median</FormLabel>
                <FormControl>
                  <Input
                    id="libMedian"
                    type="number"
                    step="any"
                    {...register("libMedian", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="libMean">Mean</FormLabel>
                <FormControl>
                  <Input
                    id="libMean"
                    type="number"
                    step="any"
                    {...register("libMean", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="libStd">Std Dev</FormLabel>
                <FormControl>
                  <Input
                    id="libStd"
                    type="number"
                    step="any"
                    {...register("libStd", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="libQ10">Q10</FormLabel>
                <FormControl>
                  <Input
                    id="libQ10"
                    type="number"
                    step="any"
                    {...register("libQ10", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="libQ90">Q90</FormLabel>
                <FormControl>
                  <Input
                    id="libQ90"
                    type="number"
                    step="any"
                    {...register("libQ90", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
              <FormItem>
                <FormLabel htmlFor="libN">N</FormLabel>
                <FormControl>
                  <Input
                    id="libN"
                    type="number"
                    step="1"
                    {...register("libN", { setValueAs: setNumberOrUndefined })}
                  />
                </FormControl>
              </FormItem>
            </div>
          )}
        </section>

        <div className="sticky bottom-0 bg-background pt-2 pb-1 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              form.reset();
              collapseSections();
            }}
          >
            Reset Form
          </Button>
          <Button type="submit">Save</Button>
        </div>
      </form>
    </Form>
  );
}
